<template>
  <div class="onboarding">
    <!-- Left section -->
    <transition name="fade" mode="out-in">
      <div class="left" :key="currentIndex">
        <div class="dots">
          <span v-for="(s, i) in slides" :key="i" :class="['dot', { active: i === currentIndex }]" />
        </div>
        <h2 class="title">{{ slides[currentIndex].title }}</h2>
        <p class="desc">{{ slides[currentIndex].desc }}</p>

        <div class="options" v-if="slides[currentIndex].options">
          <OptionButton v-for="opt in slides[currentIndex].options" :key="opt" :label="opt" @click="onSelect(opt)" />
        </div>

        <div v-if="slides[currentIndex].input" class="input-group">
          <input v-model="form[currentInputName]" :placeholder="currentInputPlaceholder" @blur="validateInput"
            class="input-box" />

          <p v-if="error[currentInputName]" class="error-msg">
            {{ error[currentInputName] }}
          </p>

          <button class="primary-btn" @click="handleFinalButton">
            {{ slides[currentIndex].button.text }}
          </button>
        </div>
      </div>
    </transition>

    <!-- Right section -->
    <div class="right">
      <transition name="slide" mode="out-in">
        <img :key="currentIndex" v-if="slides[currentIndex].image" :src="slides[currentIndex].image"
          class="onboarding-img" />
      </transition>

    </div>

    <!-- Navigation dots -->
    <button class="back-btn" @click="back" v-if="currentIndex > 0">
      <font-awesome-icon class="back-icon" :icon="['fas', 'angle-left']" />
      Back
    </button>
  </div>
</template>

<script>
import OptionButton from '../FTSOptionsButtons.vue';
import OnboardingSvg1 from '../../../assets/images/onboarding/Illustration-1.svg';
import OnboardingSvg2 from '../../../assets/images/onboarding/Illustration-2.svg';
import OnboardingSvg3 from '../../../assets/images/onboarding/Illustration-3.svg';

const slideWelcome = {
  id: 'welcome',
  title: 'Build your workflow app for free.\nNo coding needed.',
  desc: 'To begin, is this for business or personal use?',
  options: ['Business', 'Personal'],
  image: OnboardingSvg1,
};

const slideBusiness = {
  id: 'business',
  title: 'Let’s start building',
  desc: 'Pick a workspace that best fits your need.',
  options: [
    '🎯 Project management tool',
    '🆘 Issue tracker',
    '⏳ Timesheet management',
    '💰 Claim approval',
    '👀 I’m just browsing'
  ],
  image: OnboardingSvg2,
};

const slidePersonal = {
  id: 'personal',
  title: 'Let’s start building',
  desc: 'Pick a workspace that best fits your need.',
  options: [
    '📖 Reading list',
    '⛽️ Gas/petrol usage',
    '💰 Expense tracker',
    '📚 Comic book collection',
    '👀 I’m just browsing'
  ],
  image: OnboardingSvg2,
};

const slideFinal = {
  id: 'final',
  title: 'Name your workspace',
  desc: 'You can change the name or create more later.',
  input: { placeholder: 'My workspace', name: 'workspace_name' },
  button: { text: 'Create workspace' },
  image: OnboardingSvg3,
};

export default {
  components: { OptionButton },

  metaInfo() {
    return { title: 'Inistate | ' + this.slides[this.currentIndex].title };
  },
  data() {
    return {
      currentIndex: 0,
      form: {},
      error: {},
      slides: [
        slideWelcome,
        {/* this will be injected after business / personal selection */ },
        slideFinal,
      ]
    };
  },
  computed: {
    currentInputName() {
      const slide = this.slides[this.currentIndex];
      return slide && slide.input ? slide.input.name : null;
    },
    currentInputPlaceholder() {
      const slide = this.slides[this.currentIndex];
      return slide && slide.input ? slide.input.placeholder : '';
    }
  },
  methods: {
    back() {
      this.currentIndex--;
    },
    next() {
      if (this.currentIndex < this.slides.length - 1) {
        this.currentIndex++;
      }
    },
    validateInput() {
      const field = this.currentInputName;
      if (!field) return true;

      const value = this.form[field];

      if (!value || value.trim() === "") {
        this.$set(this.error, field, "This field is required");
        return false;
      }

      this.$delete(this.error, field);
      return true;
    },

    handleFinalButton() {
      if (!this.validateInput()) return;

      console.log("Submit!", this.form);
    },

    onSelect(option) {
      console.log('Selected:', option);
      if (option == 'Business') {
        this.form['type'] = 'business';
        this.slides[1] = slideBusiness;
      } else if (option == 'Personal') {
        {
          this.form['type'] = 'personal';
          this.slides[1] = slidePersonal;
        }
      } else {
        this.form['option'] = option;
      }
      this.next();
    }
  }
}
</script>

<style scoped>
.onboarding {
  display: flex;
  height: 100vh;
  position: relative;
}

.left {
  width: 50%;
  padding: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.right {
  width: 50%;
  background: #eef5f9;
  display: flex;
  justify-content: center;
  align-items: center;
}

.options {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
}

/* Fade animation (left) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Slide animation (right) */
.slide-enter-active {
  animation: slideIn 0.5s ease;
}

.slide-leave-active {
  animation: slideOut 0.5s ease;
}

@keyframes slideIn {
  from {
    transform: translateX(40px);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(-40px);
    opacity: 0;
  }
}

.dots {
  position: relative;
  bottom: 100px;
  display: flex;
  gap: 8px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ccc;
}

.dot.active {
  background: var(--fts-primary);
  font-weight: bold;
}

.title {
  font-size: 32px;
  letter-spacing: -2%;
  color: var(--fts-text);
  margin-bottom: 32px;
  white-space: pre-line;
}

.desc {
  font-size: 18px;
  color: var(--fts-text);
  margin-bottom: 32px;
  font-weight: normal;
}

.back-btn {
  position: absolute;
  bottom: 20px;
  left: 20px;
  padding: 10px 20px;
  border: none;
  background: none;
  font-size: 16px;
  cursor: pointer;
  color: var(--fts-text);
}

.back-icon {
  margin-right: 10px;
  color: var(--fts-default)
}

.input-group {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
}

.input-box {
  width: 100%;
  padding: 14px 18px;
  border-radius: 4px;
  border: 2px solid #5ba0c9;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;
  margin-bottom: 10px;
}

.input-box:focus {
  border-color: #3b89b1;
}

.error-msg {
  color: var(--fts-danger);
}

.primary-btn {
  margin-top: 22px;
  padding: 12px 24px;
  border-radius: 4px;
  background: #4b8eb1;
  color: white;
  border: none;
  cursor: pointer;
  font-size: 17px;
  font-weight: 500;
  width: fit-content;
  transition: background 0.2s;
}

.primary-btn:hover {
  background: #3a7a96;
}
</style>
